import { h } from '@stencil/core';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { DEBOUNCED_HANDLER_DELAY, BUSINESS_ID_PARAM_NAME, BUSINESS, ALLPRODUCTS, pesIconMirrorDirection, pesIconSizes, HMCPlatform, updatePageLoadData, isPublicEnvironment, isIndexDBEnabled, indexDBBreadcrumbs, getConsumerSpecificData, SprykerCommerceConnector, SprykerProductsSelectorConfig, HmcCategories, SprykerCategoriesByBusiness, setQueryParam, isEmpty, tryParse } from '@pes-ui/utils';
import { isBrowser, isMobile } from '../../globals/utils';
import { PesRouterStore } from '../../index';
const propsToParse = [
  { prop: '_breadcrumbs', target: 'breadcrumbs' },
  { prop: '_showBreadcrumbs', target: 'showBreadcrumbs' },
  { prop: '_hideOnMobile', target: 'hideOnMobile' },
  { prop: '_isPBS', target: 'isPBS' },
  { prop: '_countryCode', target: 'countryCode' },
  { prop: '_languageCode', target: 'languageCode' },
  { prop: '_businesses', target: 'businesses' },
  { prop: '_topCategories', target: 'topCategories' },
  { prop: '_appActionBlocks', target: 'appActionBlocks' },
  { prop: '_appLearnMoreLabel', target: 'appLearnMoreLabel' },
  { prop: '_appHmcLabel', target: 'appHmcLabel' },
  { prop: '_appMostViewedProductsLabel', target: 'appMostViewedProductsLabel' },
  { prop: '_appProductsForYouLabel', target: 'appProductsForYouLabel' },
  { prop: '_appProductsLabel', target: 'appProductsLabel' },
  { prop: '_appRangesLabel', target: 'appRangesLabel' },
  { prop: '_appTopRangesForLabel', target: 'appTopRangesForLabel' },
  { prop: '_appTopTangesLabel', target: 'appTopTangesLabel' },
  { prop: '_appToprunnerTypeNewLabel', target: 'appToprunnerTypeNewLabel' },
  { prop: '_appToprunnerTypePromotedLabel', target: 'appToprunnerTypePromotedLabel' },
  { prop: '_appViewProductListLink', target: 'appViewProductListLink' },
  { prop: '_breadCrumbsAllProductsLabel', target: 'breadCrumbsAllProductsLabel' },
  { prop: '_hmcToolsLink', target: 'hmcToolsLink' },
  { prop: '_productRelationsViewProdLabel', target: 'productRelationsViewProdLabel' },
  { prop: '_prpPriceLabel', target: 'prpPriceLabel' },
  { prop: '_prpTopcategoryLabel', target: 'prpTopcategoryLabel' },
  { prop: '_seeAllOffersLink', target: 'seeAllOffersLink' },
  { prop: '_viewOnLabel', target: 'viewOnLabel' },
  { prop: '_hideDownloadCenterBanner', target: 'hideDownloadCenterBanner' },
  { prop: '_hideMasterRangesBanner', target: 'hideMasterRangesBanner' },
  { prop: '_categoriesUrl', target: 'categoriesUrl' },
  { prop: '_selectorConfigUrl', target: 'selectorConfigUrl' },
  { prop: '_commerceConnectorUrl', target: 'commerceConnectorUrl' },
  { prop: '_disableBusinessLinesHashRouting', target: 'disableBusinessLinesHashRouting' },
  { prop: '_getCategoriesByBusinessId', target: 'getCategoriesByBusinessId' },
  { prop: '_getProductSelectorConfig', target: 'getProductSelectorConfig' },
];
export class AllProducts {
  handleMenuCategoryOpen() {
    this.menuCategoryOpened = true;
  }
  handleMenuCategoryClose() {
    this.menuCategoryOpened = false;
  }
  async activeBusinessIdWatcher(id) {
    this.fetchCategoriesByBusinessId(id);
    if (!isIndexDBEnabled())
      return;
    const currentPageStorageKey = id ? `${BUSINESS}/${id}` : `${ALLPRODUCTS}/`;
    try {
      const result = await indexDBBreadcrumbs.getExistingOrUpdateForBusiness({
        storageName: this.storageName,
        currentPageStorageKey,
        id,
        getActiveBusinessCallBack: this.getActiveBusiness,
      });
      if (!(result === null || result === void 0 ? void 0 : result.length))
        return;
      this.breadcrumbsFromStorage = result;
    }
    catch (e) {
      console.error(`[PES]: ${currentPageStorageKey} breadcrumbs can not be fetched or updated from indexDB ${this.storageName} storage`, e);
    }
  }
  fetchCategoriesByBusinessId(id) {
    if (!id || id in this.businessCategoriesMap) {
      return;
    }
    this.requestCategoriesByBusinessId(id).then((res) => {
      this.setBusinessCategoriesMap(id, res);
    });
  }
  async requestProductSelectorConfig() {
    if (!this.selectorConfigUrl) {
      return this.getProductSelectorConfig();
    }
    const { error, result } = await SprykerProductsSelectorConfig.fetchProductsSelectorConfig(this.selectorConfigUrl);
    if (error) {
      console.error('[PES]: Could not fetch products selector config', error);
      throw error;
    }
    return result;
  }
  async requestCommerceConnectorSettings() {
    if (!this.commerceConnectorUrl) {
      return;
    }
    const { error, result } = await SprykerCommerceConnector.getCommerceConnector({ url: this.commerceConnectorUrl });
    if (error) {
      console.error('[PES]: Could not fetch the commerce connector api', error);
      throw error;
    }
    return result;
  }
  constructor() {
    this.changeActiveLine = (id) => {
      this.activeBusinessId = id;
    };
    this.getCountryCode = (countryCode) => {
      return isPublicEnvironment() ? countryCode.toLowerCase() : countryCode;
    };
    this.setActiveBusinessId = (businessId, sourceId) => {
      this.activeBusinessId = this.activeBusinessId === businessId ? null : businessId;
      const searchParams = new URLSearchParams(window.location.search);
      if (!this.isPBS) {
        setQueryParam(searchParams, BUSINESS_ID_PARAM_NAME, sourceId);
        window.history.replaceState(null, '', `?${searchParams.toString()}${window.location.hash}`);
      }
      updatePageLoadData({ pageCategory: sourceId });
    };
    this.requestCategoriesByBusinessId = (id) => {
      if (this.categoriesUrl) {
        return this.getCategoriesByBusinessIdWithHmc(id);
      }
      return this.getCategoriesByBusinessId(id);
    };
    this.getCategoriesByBusiness = async (id) => {
      try {
        const params = new URLSearchParams(this.categoriesUrl.split('?')[1]);
        const QueryParams = Object.fromEntries(params);
        const { 'country-code': countryCode, 'language-code': languageCode } = QueryParams;
        const { error: categoriesByBusinessError, result: categoriesByBusinessResult, } = await SprykerCategoriesByBusiness.fetchCategoriesByBusiness(this.categoriesUrl.replace(':id', id));
        if (categoriesByBusinessError) {
          throw new Error(`Could not fetch categories By Business api:\n${categoriesByBusinessError.message}`);
        }
        this.categoriesByBusinessId = categoriesByBusinessResult;
        const pimIds = this.categoriesByBusinessId.map((category) => category.id).join();
        const { error: hmcCategoriesError, result: hmcCategoriesResult, } = await HmcCategories.fetchCategoriesWithHMC({
          countryCode: this.getCountryCode(countryCode),
          languageCode,
          pimIds,
          hmcBanner: 'true',
          platform: window.isPBS ? HMCPlatform.MySchneider : HMCPlatform.SE,
        });
        if (hmcCategoriesError) {
          throw new Error(`Could not fetch hmc visibility information for categories:\n${hmcCategoriesError.message}`);
        }
        this.categoriesListWithHMC = hmcCategoriesResult;
      }
      catch (error) {
        console.error('[PES] ', error);
      }
    };
    this.getCategoriesByBusinessIdWithHmc = async (id) => {
      var _a;
      await this.getCategoriesByBusiness(id);
      if (!this.categoriesByBusinessId && !this.categoriesListWithHMC)
        return [];
      const CategorieswithHMC = {};
      this.categoriesListWithHMC.forEach((hmcCategory) => {
        const { pimId, hmcBanner } = hmcCategory;
        if (hmcBanner)
          CategorieswithHMC[pimId] = hmcBanner;
      });
      return (_a = this.categoriesByBusinessId) === null || _a === void 0 ? void 0 : _a.map((category) => {
        const hmcBanner = CategorieswithHMC[category.id];
        if (hmcBanner) {
          return Object.assign(Object.assign({}, category), { hmcBanner });
        }
        return category;
      });
    };
    this.setBusinessCategoriesMap = (id, businessCategory) => {
      this.businessCategoriesMap = Object.assign(Object.assign({}, this.businessCategoriesMap), { [id]: businessCategory });
    };
    this.getActiveBusiness = (businessId) => {
      var _a;
      return (_a = this.businesses) === null || _a === void 0 ? void 0 : _a.find((business) => (business === null || business === void 0 ? void 0 : business.id) === businessId);
    };
    this.resizeWindow = () => {
      this.isMobile = isMobile();
    };
    this.debouncedHandleResize = debounce(this.resizeWindow, DEBOUNCED_HANDLER_DELAY);
    this.businesses = undefined;
    this.topCategories = undefined;
    this.appActionBlocks = undefined;
    this.appLearnMoreLabel = undefined;
    this.appHmcLabel = undefined;
    this.appMostViewedProductsLabel = undefined;
    this.appProductsForYouLabel = undefined;
    this.appProductsLabel = undefined;
    this.appRangesLabel = undefined;
    this.appTopRangesForLabel = undefined;
    this.appTopTangesLabel = undefined;
    this.appToprunnerTypeNewLabel = undefined;
    this.appToprunnerTypePromotedLabel = undefined;
    this.appViewProductListLink = undefined;
    this.hideOnMobile = undefined;
    this.breadcrumbs = undefined;
    this.breadCrumbsAllProductsLabel = undefined;
    this.hmcToolsLink = undefined;
    this.productRelationsViewProdLabel = undefined;
    this.prpPriceLabel = undefined;
    this.prpTopcategoryLabel = undefined;
    this.seeAllOffersLink = undefined;
    this.viewOnLabel = undefined;
    this.hideDownloadCenterBanner = false;
    this.hideMasterRangesBanner = false;
    this.categoriesUrl = undefined;
    this.selectorConfigUrl = undefined;
    this.commerceConnectorUrl = undefined;
    this.disableBusinessLinesHashRouting = false;
    this.getCategoriesByBusinessId = undefined;
    this.getProductSelectorConfig = undefined;
    this.isPBS = false;
    this.host = undefined;
    this.showBreadcrumbs = true;
    this.atgBusinesses = undefined;
    this.countryCode = undefined;
    this.languageCode = undefined;
    this._breadcrumbs = undefined;
    this._showBreadcrumbs = undefined;
    this._hideOnMobile = undefined;
    this._isPBS = undefined;
    this._languageCode = undefined;
    this._countryCode = undefined;
    this._businesses = undefined;
    this._topCategories = undefined;
    this._appActionBlocks = undefined;
    this._appLearnMoreLabel = undefined;
    this._appHmcLabel = undefined;
    this._appMostViewedProductsLabel = undefined;
    this._appProductsForYouLabel = undefined;
    this._appProductsLabel = undefined;
    this._appRangesLabel = undefined;
    this._appTopRangesForLabel = undefined;
    this._appTopTangesLabel = undefined;
    this._appToprunnerTypeNewLabel = undefined;
    this._appToprunnerTypePromotedLabel = undefined;
    this._appViewProductListLink = undefined;
    this._breadCrumbsAllProductsLabel = undefined;
    this._hmcToolsLink = undefined;
    this._productRelationsViewProdLabel = undefined;
    this._prpPriceLabel = undefined;
    this._prpTopcategoryLabel = undefined;
    this._seeAllOffersLink = undefined;
    this._viewOnLabel = undefined;
    this._hideDownloadCenterBanner = undefined;
    this._hideMasterRangesBanner = undefined;
    this._categoriesUrl = undefined;
    this._selectorConfigUrl = undefined;
    this._commerceConnectorUrl = undefined;
    this._disableBusinessLinesHashRouting = undefined;
    this._getCategoriesByBusinessId = undefined;
    this._getProductSelectorConfig = undefined;
    this.breadcrumbsFromStorage = [];
    this.storageName = '';
    this.isMobile = isBrowser() && isMobile();
    this.businessCategoriesMap = {};
    this.activeBusinessId = null;
    this.defaultSelectorConfig = null;
    this.menuCategoryOpened = false;
    this.commerceConnectorSettings = undefined;
    this.sprykerCategoriesByBusinessId = undefined;
    this.atgCategoriesByBusinessId = undefined;
    this.CategoriesByBusinessIdwithHMC = undefined;
    this.categoriesByBusinessId = undefined;
    this.categoriesListWithHMC = undefined;
    this.el.dir = document.documentElement.dir || 'auto';
  }
  componentWillLoad() {
    var _a, _b;
    propsToParse.forEach(({ prop, target }) => {
      var _a;
      this[target] = ((_a = tryParse(this[prop])) !== null && _a !== void 0 ? _a : this[target]);
    });
    if (!isBrowser())
      return;
    (_a = this.requestProductSelectorConfig()) === null || _a === void 0 ? void 0 : _a.then((res) => {
      this.defaultSelectorConfig = res;
    });
    (_b = this.requestCommerceConnectorSettings()) === null || _b === void 0 ? void 0 : _b.then((res) => {
      this.commerceConnectorSettings = res;
    });
    if (this.isPBS) {
      PesRouterStore.set('enableAppRouting', true);
    }
    if (!isIndexDBEnabled())
      return;
    const { storageName } = getConsumerSpecificData(this.isPBS, `${this.host}${this.countryCode}/${this.languageCode}/`);
    this.storageName = storageName;
  }
  connectedCallback() {
    window.addEventListener('resize', this.debouncedHandleResize);
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.debouncedHandleResize);
  }
  hasActionBlocks() {
    return this.appActionBlocks && !isEmpty(this.appActionBlocks);
  }
  renderTitle(currentBusiness) {
    if (this.isPBS) {
      return h("span", { class: "all-products__section-title" }, currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.name);
    }
    return (h("a", { href: currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.url, class: "all-products__section-title" }, h("span", { class: "all-products__section-title-text" }, currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.name), h("pes-icon", { class: "all-products__section-title-icon", size: pesIconSizes.MICRO, mirror: pesIconMirrorDirection.HORIZONTAL }, "arrow2_left")));
  }
  render() {
    var _a, _b, _c, _d, _e;
    const activeLine = this.getActiveBusiness(this.activeBusinessId);
    const businessCategories = (_a = this.businessCategoriesMap[this.activeBusinessId]) !== null && _a !== void 0 ? _a : [];
    const isTopCategoriesActive = !this.activeBusinessId || this.isMobile;
    return (h("div", { class: "all-products" }, this.showBreadcrumbs && this.breadcrumbs && (h("pes-breadcrumbs", { breadcrumbs: this.breadcrumbs, hideOnMobile: this.hideOnMobile, hostWithLocale: this.storageName })), this.isMobile && this.hasActionBlocks() && (h("pes-action-blocks", { actionBlocks: this.appActionBlocks, selectorConfig: this.defaultSelectorConfig })), ((_b = this.businesses) === null || _b === void 0 ? void 0 : _b.length) > 0 && (h("pes-business-lines-se", { linesList: this.businesses, selectBl: this.setActiveBusinessId, topTangesLabel: this.appTopTangesLabel, viewOnLabel: this.viewOnLabel, productsLabel: this.appProductsLabel, productRelationsViewProdLabel: this.productRelationsViewProdLabel, seeAllOffersLink: this.seeAllOffersLink, mostViewedProductsLabel: this.appMostViewedProductsLabel, learnMoreLabel: this.appLearnMoreLabel, business: businessCategories, isMobile: this.isMobile, activeLine: activeLine, disableBusinessLinesHashRouting: this.disableBusinessLinesHashRouting, changeActiveLine: this.changeActiveLine, isPBS: this.isPBS, storageName: this.storageName, breadcrumbs: this.breadcrumbs || this.breadcrumbsFromStorage })), h("main", { class: cn('all-products__main-content', {
        'all-products__main-content--menu-opened': this.menuCategoryOpened,
      }) }, h("h1", { class: "all-products__header" }, this.breadCrumbsAllProductsLabel), ((_c = this.topCategories) === null || _c === void 0 ? void 0 : _c.length) > 0 && (h("section", { class: cn('all-products__section', {
        'all-products__section--inactive': !isTopCategoriesActive,
      }) }, h("pes-top-categories", { class: "all-products__top-categories", isActive: isTopCategoriesActive, list: this.topCategories, actionBlocks: this.appActionBlocks, viewOnLabel: this.viewOnLabel, cardCountLabel: this.appRangesLabel, isMobile: this.isMobile, selectorConfig: this.defaultSelectorConfig, hideDownloadCenterBanner: this.hideDownloadCenterBanner, hideMasterRangesBanner: this.hideMasterRangesBanner, isPBS: this.isPBS, storageName: this.storageName, breadcrumbs: this.breadcrumbs || this.breadcrumbsFromStorage }))), ((_d = this.businesses) === null || _d === void 0 ? void 0 : _d.length) > 0
      && ((_e = this.businesses) === null || _e === void 0 ? void 0 : _e.map((currentBusiness) => {
        var _a;
        const isActive = this.activeBusinessId === (currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.id) && !this.isMobile;
        return (h("section", { key: (_a = currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.id) !== null && _a !== void 0 ? _a : '', class: cn('all-products__section', {
            'all-products__section--inactive': !isActive,
          }) }, h("div", { class: "all-products__section-title-wrapper" }, this.renderTitle(currentBusiness)), h("pes-menu-category", { class: "all-products__menu-category", topTangesLabel: this.appTopTangesLabel, viewOnLabel: this.viewOnLabel, productRelationsViewProdLabel: this.productRelationsViewProdLabel, seeAllOffersLink: this.seeAllOffersLink, mostViewedProductsLabel: this.appMostViewedProductsLabel, menuList: currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.categories, business: businessCategories, isMobile: this.isMobile, commerceConnectorSettings: this.commerceConnectorSettings, isPBS: this.isPBS, host: this.host, storageName: this.storageName, breadcrumbs: this.breadcrumbs || this.breadcrumbsFromStorage }), h("pes-top-runners", { class: "all-products__top-runners", isActive: isActive, list: currentBusiness === null || currentBusiness === void 0 ? void 0 : currentBusiness.toprunners, viewOnLabel: this.viewOnLabel, productsLabel: this.appProductsLabel, isMobile: this.isMobile, storageName: this.storageName, breadcrumbs: this.breadcrumbs || this.breadcrumbsFromStorage })));
      })))));
  }
  static get is() { return "pes-all-products"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["all-products.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["all-products.css"]
    };
  }
  static get properties() {
    return {
      "businesses": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "Array<CategoriesApi.ProductsAllBusinesses>",
          "resolved": "ProductsAllBusinesses[]",
          "references": {
            "Array": {
              "location": "global"
            },
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "topCategories": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "Array<CategoriesApi.ProductsAllTopCategories>",
          "resolved": "ProductsAllTopCategories[]",
          "references": {
            "Array": {
              "location": "global"
            },
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "appActionBlocks": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "CategoriesApi.ActionBlocksHashMap",
          "resolved": "{ [x: string]: ActionBlock; }",
          "references": {
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "appLearnMoreLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-learn-more-label",
        "reflect": false
      },
      "appHmcLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-hmc-label",
        "reflect": false
      },
      "appMostViewedProductsLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-most-viewed-products-label",
        "reflect": false
      },
      "appProductsForYouLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-products-for-you-label",
        "reflect": false
      },
      "appProductsLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-products-label",
        "reflect": false
      },
      "appRangesLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-ranges-label",
        "reflect": false
      },
      "appTopRangesForLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-top-ranges-for-label",
        "reflect": false
      },
      "appTopTangesLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-top-tanges-label",
        "reflect": false
      },
      "appToprunnerTypeNewLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-toprunner-type-new-label",
        "reflect": false
      },
      "appToprunnerTypePromotedLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-toprunner-type-promoted-label",
        "reflect": false
      },
      "appViewProductListLink": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "app-view-product-list-link",
        "reflect": false
      },
      "hideOnMobile": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "hide-on-mobile",
        "reflect": false
      },
      "breadcrumbs": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "TBreadcrumbs[]",
          "resolved": "TBreadcrumbs[]",
          "references": {
            "TBreadcrumbs": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "breadCrumbsAllProductsLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "bread-crumbs-all-products-label",
        "reflect": false
      },
      "hmcToolsLink": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "hmc-tools-link",
        "reflect": false
      },
      "productRelationsViewProdLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "product-relations-view-prod-label",
        "reflect": false
      },
      "prpPriceLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "prp-price-label",
        "reflect": false
      },
      "prpTopcategoryLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "prp-topcategory-label",
        "reflect": false
      },
      "seeAllOffersLink": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "see-all-offers-link",
        "reflect": false
      },
      "viewOnLabel": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "view-on-label",
        "reflect": false
      },
      "hideDownloadCenterBanner": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "force hide the Download Center action block"
        },
        "attribute": "hide-download-center-banner",
        "reflect": false,
        "defaultValue": "false"
      },
      "hideMasterRangesBanner": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "force hide the Master Ranges action block"
        },
        "attribute": "hide-master-ranges-banner",
        "reflect": false,
        "defaultValue": "false"
      },
      "categoriesUrl": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "url for requesting categories, it's require to\nuse either categoriesUrl or getCategoriesByBusinessId"
        },
        "attribute": "categories-url",
        "reflect": false
      },
      "selectorConfigUrl": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "url for requesting product selector config, it's require to\nuse either selectorConfigUrl or getProductSelectorConfig"
        },
        "attribute": "selector-config-url",
        "reflect": false
      },
      "commerceConnectorUrl": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Url for requesting settings for commerce connector settings."
        },
        "attribute": "commerce-connector-url",
        "reflect": false
      },
      "disableBusinessLinesHashRouting": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "disable hash routing for changing business-lines tabs"
        },
        "attribute": "disable-business-lines-hash-routing",
        "reflect": false,
        "defaultValue": "false"
      },
      "getCategoriesByBusinessId": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "(\n    id: string,\n  ) => Promise<CategoriesApi.ProductsAllCategoriesCategory[]>",
          "resolved": "(id: string) => Promise<ProductsAllCategoriesCategory[]>",
          "references": {
            "Promise": {
              "location": "global"
            },
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "getProductSelectorConfig": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "() => Promise<TScProductSelectorConfig>",
          "resolved": "() => Promise<TScProductSelectorConfig>",
          "references": {
            "Promise": {
              "location": "global"
            },
            "TScProductSelectorConfig": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isPBS": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Defines whether the component is integrated to PBS."
        },
        "attribute": "is-p-b-s",
        "reflect": false,
        "defaultValue": "false"
      },
      "host": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "host",
        "reflect": false
      },
      "showBreadcrumbs": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "show-breadcrumbs",
        "reflect": false,
        "defaultValue": "true"
      },
      "atgBusinesses": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Array<CategoriesApi.ProductsAllBusinesses>",
          "resolved": "ProductsAllBusinesses[]",
          "references": {
            "Array": {
              "location": "global"
            },
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "countryCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [{
              "name": "example",
              "text": ": us"
            }],
          "text": "countryCode"
        },
        "attribute": "country-code",
        "reflect": false
      },
      "languageCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [{
              "name": "example",
              "text": ": en"
            }],
          "text": "languageCode"
        },
        "attribute": "language-code",
        "reflect": false
      },
      "_breadcrumbs": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-breadcrumbs",
        "reflect": false
      },
      "_showBreadcrumbs": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-show-breadcrumbs",
        "reflect": false
      },
      "_hideOnMobile": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-hide-on-mobile",
        "reflect": false
      },
      "_isPBS": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-is-pbs",
        "reflect": false
      },
      "_languageCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-language-code",
        "reflect": false
      },
      "_countryCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-country-code",
        "reflect": false
      },
      "_businesses": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-businesses",
        "reflect": false
      },
      "_topCategories": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-top-categories",
        "reflect": false
      },
      "_appActionBlocks": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-action-blocks",
        "reflect": false
      },
      "_appLearnMoreLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-learn-more-label",
        "reflect": false
      },
      "_appHmcLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-hmc-label",
        "reflect": false
      },
      "_appMostViewedProductsLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-most-viewed-products-label",
        "reflect": false
      },
      "_appProductsForYouLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-products-for-you-label",
        "reflect": false
      },
      "_appProductsLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-products-label",
        "reflect": false
      },
      "_appRangesLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-ranges-label",
        "reflect": false
      },
      "_appTopRangesForLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-top-ranges-for-label",
        "reflect": false
      },
      "_appTopTangesLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-top-tanges-label",
        "reflect": false
      },
      "_appToprunnerTypeNewLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-toprunner-type-new-label",
        "reflect": false
      },
      "_appToprunnerTypePromotedLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-toprunner-type-promoted-label",
        "reflect": false
      },
      "_appViewProductListLink": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-app-view-product-list-link",
        "reflect": false
      },
      "_breadCrumbsAllProductsLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-bread-crumbs-all-products-label",
        "reflect": false
      },
      "_hmcToolsLink": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-hmc-tools-link",
        "reflect": false
      },
      "_productRelationsViewProdLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-product-relations-view-prod-label",
        "reflect": false
      },
      "_prpPriceLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-prp-price-label",
        "reflect": false
      },
      "_prpTopcategoryLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-prp-topcategory-label",
        "reflect": false
      },
      "_seeAllOffersLink": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-see-all-offers-link",
        "reflect": false
      },
      "_viewOnLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-view-on-label",
        "reflect": false
      },
      "_hideDownloadCenterBanner": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-hide-download-center-banner",
        "reflect": false
      },
      "_hideMasterRangesBanner": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-hide-master-ranges-banner",
        "reflect": false
      },
      "_categoriesUrl": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-categories-url",
        "reflect": false
      },
      "_selectorConfigUrl": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-selector-config-url",
        "reflect": false
      },
      "_commerceConnectorUrl": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-commerce-connector-url",
        "reflect": false
      },
      "_disableBusinessLinesHashRouting": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-disable-business-lines-hash-routing",
        "reflect": false
      },
      "_getCategoriesByBusinessId": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-get-categories-by-business-id",
        "reflect": false
      },
      "_getProductSelectorConfig": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-get-product-selector-config",
        "reflect": false
      }
    };
  }
  static get states() {
    return {
      "breadcrumbsFromStorage": {},
      "storageName": {},
      "isMobile": {},
      "businessCategoriesMap": {},
      "activeBusinessId": {},
      "defaultSelectorConfig": {},
      "menuCategoryOpened": {},
      "commerceConnectorSettings": {},
      "sprykerCategoriesByBusinessId": {},
      "atgCategoriesByBusinessId": {},
      "CategoriesByBusinessIdwithHMC": {},
      "categoriesByBusinessId": {},
      "categoriesListWithHMC": {}
    };
  }
  static get elementRef() { return "el"; }
  static get watchers() {
    return [{
        "propName": "activeBusinessId",
        "methodName": "activeBusinessIdWatcher"
      }];
  }
  static get listeners() {
    return [{
        "name": "pdpMenuCategoryDidOpen",
        "method": "handleMenuCategoryOpen",
        "target": "document",
        "capture": false,
        "passive": false
      }, {
        "name": "pdpMenuCategoryDidClose",
        "method": "handleMenuCategoryClose",
        "target": "document",
        "capture": false,
        "passive": false
      }];
  }
}
